/**
 * Точная арифметика над денежными строками.
 *
 * Ни одна функция здесь не превращает сумму в `number`. Причина простая:
 * у Monero двенадцать знаков после запятой, а `number` в JavaScript —
 * это double, у которого на такую длину уже не хватает мантиссы.
 * `0.123456789012 - 0.000000000001` в числах даст не тот результат,
 * и покупатель увидит «доплатите» на неверную сумму. Недоплата
 * с нулевым допуском означает застрявшую заявку: деньги мерчанту
 * лягут, а заказ не закроется.
 *
 * Поэтому всё считается в целых (BigInt) на общем масштабе.
 */

const DECIMAL_RE = /^-?\d+(\.\d+)?$/

interface Scaled {
  units: bigint
  scale: number
}

function parse(text: string): Scaled | null {
  const trimmed = text.trim()
  if (!DECIMAL_RE.test(trimmed)) return null
  const negative = trimmed.startsWith('-')
  const body = negative ? trimmed.slice(1) : trimmed
  const dot = body.indexOf('.')
  const whole = dot === -1 ? body : body.slice(0, dot)
  const frac = dot === -1 ? '' : body.slice(dot + 1)
  const units = BigInt(whole + frac)
  return { units: negative ? -units : units, scale: frac.length }
}

function align(a: Scaled, b: Scaled): { a: bigint; b: bigint; scale: number } {
  const scale = Math.max(a.scale, b.scale)
  const lift = (value: Scaled) => value.units * 10n ** BigInt(scale - value.scale)
  return { a: lift(a), b: lift(b), scale }
}

function render(units: bigint, scale: number): string {
  const negative = units < 0n
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0')
  const whole = digits.slice(0, digits.length - scale)
  const frac = scale === 0 ? '' : digits.slice(digits.length - scale).replace(/0+$/, '')
  const body = frac ? `${whole}.${frac}` : whole
  return negative ? `-${body}` : body
}

/**
 * `a - b`. `null`, если хотя бы одна строка не разобралась — тогда
 * вызывающий обязан показать обе суммы как есть, а не выдуманную разницу.
 */
export function subtractDecimal(a: string, b: string): string | null {
  const left = parse(a)
  const right = parse(b)
  if (!left || !right) return null
  const aligned = align(left, right)
  return render(aligned.a - aligned.b, aligned.scale)
}

/** -1, 0, 1 или null, если не разобралось. */
export function compareDecimal(a: string, b: string): number | null {
  const left = parse(a)
  const right = parse(b)
  if (!left || !right) return null
  const aligned = align(left, right)
  if (aligned.a < aligned.b) return -1
  if (aligned.a > aligned.b) return 1
  return 0
}

/** Больше нуля? Используется, чтобы отличить «ещё ничего не пришло». */
export function isPositive(a: string): boolean {
  const value = parse(a)
  return value !== null && value.units > 0n
}


/**
 * Сколько знаков после запятой сеть вообще умеет хранить.
 *
 * Взято из бекенда (`_SCALE`), и расхождение здесь дороже, чем кажется:
 * если фронт пропустит `0.0000000001 BTC`, бекенд округлит сумму до
 * своих восьми знаков, подпишет транзакцию на другое число, а человек
 * будет искать пропавшие разряды в поддержке.
 */
export const SCALE: Record<string, number> = {
  btc: 8, ltc: 8, xmr: 12, gram: 9, usdt_trc20: 6, usdt_ton: 6,
}

/**
 * Обрезать ВНИЗ до точности сети. «Весь баланс» с хвостом мельче сети
 * иначе не проходит собственную проверку суммы, а округление вверх
 * дало бы сумму больше доступного.
 */
export function floorToScale(text: string, currency: string): string | null {
  const value = parse(text)
  const scale = SCALE[currency]
  if (value === null || scale === undefined) return value === null ? null : text
  if (value.scale <= scale) return render(value.units, value.scale)
  const cut = 10n ** BigInt(value.scale - scale)
  return render(value.units / cut, scale)
}

/**
 * Число ли это вообще и влезает ли в точность сети.
 *
 * Возвращает текст ошибки или `null`. Отдельная функция, а не regex
 * по месту: правило одно для отправки, автовывода и суммы счёта,
 * а размноженное по трём экранам оно разойдётся на третьем.
 */
export function checkAmount(text: string, currency: string): string | null {
  const trimmed = text.trim().replace(',', '.')
  if (trimmed === '') return 'Укажите сумму'
  if (!DECIMAL_RE.test(trimmed)) return 'Только цифры и точка'
  if (trimmed.startsWith('-')) return 'Сумма не может быть отрицательной'
  if (!isPositive(trimmed)) return 'Сумма должна быть больше нуля'
  const scale = SCALE[currency]
  const frac = trimmed.includes('.') ? trimmed.split('.')[1] ?? '' : ''
  if (scale !== undefined && frac.length > scale) {
    return `В этой сети не больше ${scale} знаков после запятой`
  }
  return null
}

/** `a + b` теми же правилами, что и вычитание. */
export function addDecimal(a: string, b: string): string | null {
  const left = parse(a)
  const right = parse(b)
  if (left === null || right === null) return null
  const { a: x, b: y, scale } = align(left, right)
  return render(x + y, scale)
}

/** Привести введённое человеком к виду, который поймёт бекенд. */
export function normalizeAmount(text: string): string {
  return text.trim().replace(',', '.')
}
